<template>
  <div class="crm-reports">
    <div class="crm__filters">
      <CrmDateField v-model="dateFrom" label="Період від" @input="load" />
      <CrmDateField v-model="dateTo" label="Період до" @input="load" />
      <div class="crm-reports__presets">
        <v-chip v-for="item in presets" :key="item.key" small :outlined="preset !== item.key" :color="preset === item.key ? 'primary' : undefined" @click="applyPreset(item.key)">{{ item.text }}</v-chip>
      </div>
      <v-btn color="primary" depressed :loading="exporting" @click="downloadExcel">Excel</v-btn>
    </div>

    <v-skeleton-loader v-if="!report" type="article"></v-skeleton-loader>
    <template v-else>
      <p class="grey--text">Період: {{ dateFrom ? "" : "весь час, " }}{{ report.period.from_human }} – {{ report.period.to_human }}</p>

      <div class="crm-reports__grid">
        <section class="crm-reports__card">
          <h3>Дохід від підписок</h3>
          <p v-if="!report.income.by_currency.length" class="grey--text">Оплат немає.</p>
          <div v-for="row in report.income.by_currency" :key="row.currency" class="crm-reports__big">{{ money(row.amount) }} {{ row.currency }}</div>
          <p class="caption">Оплат: {{ report.income.payments }}. Невдалих списань: {{ report.income.failures }}. Безкоштовний показ від адміна не враховано.</p>
          <table v-if="report.income.by_month.length > 1" class="crm-reports__table">
            <tr v-for="row in report.income.by_month" :key="row.month + row.currency"><td>{{ row.month }}</td><td>{{ money(row.amount) }} {{ row.currency }}</td><td>{{ row.payments }} опл.</td></tr>
          </table>
        </section>

        <section class="crm-reports__card">
          <h3>Психологи за статусами</h3>
          <table class="crm-reports__table">
            <tr v-for="(label, key) in statusLabels" :key="key"><td>{{ label }}</td><td>{{ report.statuses[key] || 0 }}</td></tr>
          </table>
          <p class="caption">Зараз, незалежно від періоду. Повні списки з ПІБ, телефоном і email — в Excel (аркуш «За статусами»).</p>
        </section>

        <section class="crm-reports__card">
          <h3>Заявки</h3>
          <table class="crm-reports__table">
            <tr><td>Усього</td><td>{{ report.leads.totals.total }}</td></tr>
            <tr><td>З відповіддю психолога</td><td>{{ report.leads.totals.answered }}</td></tr>
            <tr><td>Зв'язались</td><td>{{ report.leads.totals.contacted }}</td></tr>
            <tr><td>Консультацій призначено</td><td>{{ report.leads.totals.scheduled }}</td></tr>
            <tr><td>Не вдалося зв'язатися</td><td>{{ report.leads.totals.unreachable }}</td></tr>
          </table>
        </section>

        <section class="crm-reports__card">
          <h3>Перегляди анкет</h3>
          <table class="crm-reports__table">
            <tr><td>Загальні (кожне відкриття)</td><td>{{ report.views.total_opens }}</td></tr>
            <tr><td>Унікальні відвідувачі</td><td>{{ report.views.total_visitors }}</td></tr>
            <tr><td>1 на відвідувача за день</td><td>{{ report.views.total_views }}</td></tr>
            <tr><td>Кліки по контактах</td><td>{{ report.views.total_clicks }}</td></tr>
          </table>
          <p class="caption">{{ trackingNote }}</p>
        </section>
      </div>

      <section class="crm-reports__card mt-4">
        <h3>Джерела</h3>
        <div class="crm-reports__sources">
          <div v-for="(rows, who) in report.sources" :key="who">
            <div class="font-weight-medium mb-1">{{ who }}</div>
            <p v-if="!rows.length" class="grey--text caption">Даних немає</p>
            <table v-else class="crm-reports__table">
              <tr v-for="row in rows" :key="row.key"><td>{{ row.label }}</td><td>{{ row.count }}</td></tr>
            </table>
          </div>
        </div>
      </section>

      <section class="crm-reports__card mt-4">
        <h3>Нові психологи ({{ report.new_psychologists.length }})</h3>
        <v-data-table :headers="newHeaders" :items="report.new_psychologists" dense :items-per-page="10" no-data-text="Немає" :footer-props="{ itemsPerPageText: 'Рядків' }"></v-data-table>
      </section>

      <section class="crm-reports__card mt-4">
        <h3>Відписки з причинами ({{ report.unsubscribes.length }})</h3>
        <v-data-table :headers="unsubHeaders" :items="report.unsubscribes" dense :items-per-page="10" no-data-text="Немає" :footer-props="{ itemsPerPageText: 'Рядків' }"></v-data-table>
      </section>

      <section class="crm-reports__card mt-4">
        <h3>Заявки по психологах</h3>
        <v-data-table :headers="leadHeaders" :items="report.leads.by_psychologist" dense :items-per-page="10" no-data-text="Немає" :footer-props="{ itemsPerPageText: 'Рядків' }"></v-data-table>
      </section>

      <section class="crm-reports__card mt-4">
        <h3>Перегляди анкет по психологах: на сайті ({{ viewsOnSite.length }})</h3>
        <v-data-table :headers="viewHeaders" :items="viewsOnSite" dense :items-per-page="10" no-data-text="Немає" :footer-props="{ itemsPerPageText: 'Рядків' }"></v-data-table>
      </section>

      <!-- inactive, archived and new psychologists are kept in the report, but apart from the ones on the site -->
      <v-expansion-panels v-if="viewsOffSite.length" class="mt-4" flat>
        <v-expansion-panel class="crm-reports__card crm-reports__panel">
          <v-expansion-panel-header class="px-0 py-0">
            <h3 class="mb-0">Не на сайті: неактивні й архів ({{ viewsOffSite.length }})</h3>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-data-table :headers="viewOffHeaders" :items="viewsOffSite" dense :items-per-page="10" no-data-text="Немає" :footer-props="{ itemsPerPageText: 'Рядків' }"></v-data-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
  </div>
</template>

<script>
import ApiClient from "@/libs/http-client";
import CrmDateField from "./CrmDateField";
import { download } from "./download";

const today = new Date();
const iso = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

export default {
  name: "CrmReports",
  components: { CrmDateField },
  data() {
    return {
      dateFrom: iso(new Date(today.getFullYear(), today.getMonth(), 1)),
      dateTo: iso(today),
      report: null,
      exporting: false,
      presets: [
        { key: "month", text: "Цей місяць" },
        { key: "last-month", text: "Минулий місяць" },
        { key: "quarter", text: "3 місяці" },
        { key: "year", text: "Цей рік" },
        { key: "all", text: "Весь період" },
      ],
      statusLabels: { active: "Активні", inactive: "Неактивні", archive: "Архів", new: "Нові (не схвалені)" },
      newHeaders: [
        { text: "Дата", value: "date" },
        { text: "ПІБ", value: "name" },
        { text: "Телефон", value: "phone" },
        { text: "Email", value: "email" },
        { text: "Джерело", value: "source" },
      ],
      unsubHeaders: [
        { text: "Дата", value: "date" },
        { text: "ПІБ", value: "name" },
        { text: "Телефон", value: "phone" },
        { text: "Email", value: "email" },
        { text: "Причина", value: "reason" },
      ],
      leadHeaders: [
        { text: "Психолог", value: "name" },
        { text: "Заявок", value: "total" },
        { text: "З відповіддю", value: "answered" },
        { text: "Консультацій", value: "scheduled" },
        { text: "Не вдалося", value: "unreachable" },
      ],
      viewHeaders: [
        { text: "Психолог", value: "name" },
        { text: "Загальні", value: "opens" },
        { text: "Унікальні", value: "visitors" },
        { text: "1 на відвідувача за день", value: "views" },
        { text: "Кліки по контактах", value: "clicks" },
        { text: "Заявок", value: "leads" },
      ],
      viewOffHeaders: [
        { text: "Психолог", value: "name" },
        { text: "Статус", value: "status_label" },
        { text: "Загальні", value: "opens" },
        { text: "Унікальні", value: "visitors" },
        { text: "1 на відвідувача за день", value: "views" },
        { text: "Кліки по контактах", value: "clicks" },
        { text: "Заявок", value: "leads" },
      ],
    };
  },
  computed: {
    // the preset that the picked dates match (none for a custom period)
    preset() {
      const match = this.presets.find((item) => {
        const [from, to] = this.presetDates(item.key);
        return from === this.dateFrom && to === this.dateTo;
      });
      return match ? match.key : null;
    },
    viewsOnSite() {
      return this.report.views.by_psychologist.filter((row) => row.on_site);
    },
    viewsOffSite() {
      return this.report.views.by_psychologist.filter((row) => !row.on_site);
    },
    trackingNote() {
      const since = this.report.views.tracked_since;
      return since
        ? `Загальні й унікальні рахуються з ${since}; до цієї дати є лише «1 на відвідувача за день».`
        : "Загальні й унікальні почнуть рахуватися з першого перегляду анкети.";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    presetDates(key) {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      if (key === "last-month") return [iso(new Date(year, month - 1, 1)), iso(new Date(year, month, 0))];
      if (key === "quarter") return [iso(new Date(year, month - 2, 1)), iso(now)];
      if (key === "year") return [iso(new Date(year, 0, 1)), iso(now)];
      // no start date: the whole time
      if (key === "all") return [null, iso(now)];
      return [iso(new Date(year, month, 1)), iso(now)];
    },
    applyPreset(key) {
      [this.dateFrom, this.dateTo] = this.presetDates(key);
      this.load();
    },
    params() {
      return { date_from: this.dateFrom || undefined, date_to: this.dateTo || undefined };
    },
    load() {
      ApiClient.get("api/admin/crm/reports", { params: this.params() }).then((response) => {
        if (response) this.report = response.data.data;
      });
    },
    downloadExcel() {
      this.exporting = true;
      download("api/admin/crm/reports/export", this.params(), "crm-report").finally(() => {
        this.exporting = false;
      });
    },
    money(value) {
      // the site's font draws the no-break space of uk-UA digit groups far too wide: a plain space, kept on one line by CSS
      return Number(value).toLocaleString("uk-UA", { maximumFractionDigits: 2 }).replace(/[\u00a0\u202f]/g, " ");
    },
  },
};
</script>

<style lang="scss" scoped>
.crm-reports__presets {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.crm-reports__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}
.crm-reports__card {
  padding: 16px;
  border: 1px solid #E3EAF0;
  border-radius: 8px;
  background: #FFFFFF;
}
.crm-reports__card h3 {
  margin-bottom: 8px;
  color: #575756;
  font-size: 17px;
  font-weight: 500;
}
.crm-reports__big {
  color: #00A3B7;
  font-size: 28px;
  font-weight: 500;
  white-space: nowrap;
}
.crm-reports__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.crm-reports__table td {
  padding: 4px 0;
  border-bottom: 1px solid #F0F3F6;
}
.crm-reports__table td:not(:first-child) {
  text-align: right;
  white-space: nowrap;
}
.crm-reports__panel::before {
  box-shadow: none;
}
.crm-reports__sources {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}
</style>
